import { useCallback, useMemo } from 'react'

import { AdvancedMarker, useMap } from '@vis.gl/react-google-maps'

import type { TLote } from '@/features/produtos/interfaces/produto.types'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip'
import { cn } from '@/shared/lib'
import { formatToBRL } from '@/shared/utils/currency.utils'

interface LoteMarkerProps {
  lote: TLote
  minZoom: number
  maxZoom: number
  onClick: () => void
}

const red = 'bg-red-100 text-red-900'
const light_orange = 'bg-orange-100 text-orange-800'
const dark_orange = 'bg-orange-200 text-orange-600'
const green = 'bg-green-100 text-green-900'

const statusClasses: Record<TLote['tipoLegendaStatus'], string> = {
  Disponivel: green,
  Indisponivel: red,
  ReservadoPorProposta: light_orange,
  ReservadoPeloVendedor: dark_orange,
}

export const LoteMarker = ({ lote, minZoom, maxZoom, onClick }: LoteMarkerProps) => {
  const map = useMap()
  const badgeVariant = statusClasses[lote.tipoLegendaStatus]

  const getMarkerSize = useCallback(
    (zoom: number) => {
      const zoomRange = maxZoom - minZoom
      const zoomProgress = Math.min(Math.max((zoom - minZoom) / zoomRange, 0), 1)

      if (zoomProgress === 1) {
        return {
          size: 'large' as const,
          classes: 'w-15 h-15 text-base',
        }
      } else if (zoomProgress >= 0.5) {
        return {
          size: 'regular' as const,
          classes: 'w-12 h-12 text-sm',
        }
      } else {
        return {
          size: 'small' as const,
          classes: 'w-6 h-6 text-xs',
        }
      }
    },
    [minZoom, maxZoom],
  )

  const markerSize = getMarkerSize(map?.getZoom() ?? 20)

  const center = useMemo(() => {
    if (!lote.coordenadas || lote.coordenadas.length === 0) {
      return { lat: 0, lng: 0 }
    }
    let lat = 0.0
    let lng = 0.0

    lote.coordenadas.forEach((coord) => {
      lat += coord.latitude
      lng += coord.longitude
    })

    return {
      lat: lat / lote.coordenadas.length,
      lng: lng / lote.coordenadas.length,
    }
  }, [lote.coordenadas])

  return (
    <AdvancedMarker position={center} onClick={onClick} anchorPoint={['50%', '50%']}>
      <Tooltip key={lote.idExterno}>
        <TooltipTrigger asChild>
          <div
            className={cn(
              markerSize.classes,
              badgeVariant,
              'flex cursor-pointer items-center justify-center rounded-full font-medium shadow-md',
            )}
          >
            {lote.descricao.slice(0, markerSize.size === 'small' ? 2 : 3)}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={-5}>
          <p>{formatToBRL(lote.valor || 0)}</p>
        </TooltipContent>
      </Tooltip>
    </AdvancedMarker>
  )
}
